import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiSearch, FiFilter, FiFileText, FiClock, FiCheckCircle, FiAlertCircle, FiChevronRight, FiDownload, FiEdit3, FiEye, FiX, FiRefreshCw, FiLayers, FiUploadCloud } from 'react-icons/fi';
import styled from 'styled-components';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import { useOutletContext } from 'react-router-dom';
import SlidingDrawer from '../../components/SlidingDrawer';

// --- Styled Components (Masterpiece Theme & Refinements) ---
const Container = styled.div`
    font-family: "Outfit", sans-serif;
    max-width: 1400px;
    margin: 0 auto;
    overflow-x: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    marginBottom: 30px;

    h1 {
        margin: 0 0 8px 0;
        font-size: 32px;
        fontWeight: 800;
        color: #2C1A10;
    }

    p {
        margin: 0;
        color: #999;
        fontSize: 16px;
    }
`;

const SearchContainer = styled.div`
    position: relative;
    margin-top: 45px;
    margin-bottom: 45px;
    display: flex;
    gap: 16px;
`;

const SearchInputWrapper = styled.div`
    position: relative;
    flex: 1;

    .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #C0722C;
        display: flex;
        z-index: 2;
    }

    input {
        width: 100%;
        padding: 16px 20px 16px 48px;
        border-radius: 12px;
        border: 1.5px solid #E5E5E5;
        font-size: 16px;
        box-sizing: border-box;
        outline: none;
        transition: all 0.3s ease;
        background-color: #FAFAFA;
        font-family: inherit;
        color: #333;

        &:focus {
            border-color: #C0722C;
            box-shadow: 0 0 0 4px rgba(192, 114, 44, 0.1);
            background-color: #fff;
        }
    }
`;

const ActionButton = styled.button`
    padding: 14px 28px;
    background-color: #2C1A10;
    color: white;
    border: none;
    border-radius: 16px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(0,0,0,0.15);
    }
`;


const TableContainer = styled.div`
    background: white;
    border-radius: 28px;
    box-shadow: 0 15px 45px rgba(0,0,0,0.03);
    overflow: hidden;
    border: 1px solid #F0F0F0;
    min-height: 400px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    text-align: left;

    thead {
        background-color: #FDF8F3;
    }

    th {
        padding: 25px 30px;
        color: #888;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1.5px;
    }

    td {
        padding: 25px 30px;
        border-bottom: 1px solid #F5F5F5;
        vertical-align: middle;
    }

    tr:hover td {
        background-color: #FAFAFA;
    }
`;

const StatusBadge = styled.span`
    padding: 6px 14px;
    border-radius: 10px;
    background-color: ${props => props.bg || '#FDF8F3'};
    color: ${props => props.color || '#E65100'};
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    border: 1px solid ${props => props.border || 'rgba(230, 81, 0, 0.2)'};
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const VersionBadge = styled.span`
    background: #EFF6FF;
    color: #3B82F6;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #DBEAFE;
`;

const DataBlock = styled.div`
    margin-bottom: 20px;
    padding: 15px;
    background-color: #F9F9F9;
    border-radius: 12px;
    border: 1px solid #F0F0F0;

    label {
        display: block;
        font-size: 10px;
        font-weight: 800;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        margin-bottom: 8px;
    }

    .value {
        font-size: 15px;
        color: #2C1A10;
        font-weight: 600;
        line-height: 1.5;
    }
`;

const UploadZone = styled.div`
    border: 2px dashed #E5E5E5;
    border-radius: 16px;
    padding: 30px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #FAFAFA;

    &:hover {
        border-color: #C0722C;
        background: #FDF8F3;
    }

    input {
        display: none;
    }

    .icon {
        color: #C0722C;
        margin-bottom: 12px;
        font-size: 32px;
    }

    .text {
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
    }

    .subtext {
        font-size: 12px;
        color: #888;
    }
`;

const COLORS = {
    DRAFT: { bg: '#FDF8F3', text: '#E65100', border: 'rgba(230, 81, 0, 0.2)' },
    SUBMITTED: { bg: '#E3F2FD', text: '#1565C0', border: 'rgba(21, 101, 192, 0.2)' },
    TABLED: { bg: '#F3E5F5', text: '#7B1FA2', border: 'rgba(123, 31, 162, 0.2)' },
    PASSED: { bg: '#E8F5E9', text: '#2E7D32', border: 'rgba(46, 125, 50, 0.2)' },
    REJECTED: { bg: '#FFEBEE', text: '#C62828', border: 'rgba(198, 40, 40, 0.2)' },
    WITHDRAWN: { bg: '#ECEFF1', text: '#455A64', border: 'rgba(69, 90, 100, 0.2)' }
};

const MinisterBills = () => {
    const { currentDept } = useOutletContext();
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [editingBill, setEditingBill] = useState(null);
    const [billVersions, setBillVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        type: 'Ordinary',
        description: '',
        documentUrl: '',
        language: 'English',
        session: '',
    });

    useEffect(() => {
        if (currentDept) fetchBills();
    }, [currentDept]);

    useEffect(() => {
        if (selectedVersion) {
            const v = billVersions.find(b => b._id === selectedVersion);
            if (v) setEditingBill(v);
        }
    }, [selectedVersion, billVersions]);

    const fetchBills = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/bills`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBills(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchVersions = async (billId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/bills/${billId}/versions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBillVersions(res.data);
            return res.data;
        } catch (err) {
            console.error('Failed to fetch versions', err);
            return [];
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            return Swal.fire('Invalid File', 'Only PDF documents are allowed.', 'warning');
        }

        const data = new FormData();
        data.append('file', file);

        setUploading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/upload`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setFormData({ ...formData, documentUrl: res.data.url });
            Swal.fire('Uploaded', 'Bill document synchronized successfully.', 'success');
        } catch (err) {
            Swal.fire('Upload Failed', 'Could not upload the document.', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingBill(null);
        setViewMode(false);
        setFormData({ title: '', type: 'Ordinary', description: '', documentUrl: '', language: 'English', session: '' });
        setIsDrawerOpen(true);
    };

    const handleEdit = (bill) => {
        setEditingBill(bill);
        setViewMode(false);
        setFormData({
            title: bill.title,
            type: bill.type,
            description: bill.description,
            documentUrl: bill.documentUrl,
            language: bill.language || 'English',
            session: bill.session || '',
            parentBillId: bill._id
        });
        setIsDrawerOpen(true);
    };

    const handleView = async (bill) => {
        const versions = await fetchVersions(bill._id);
        setEditingBill(bill);
        setSelectedVersion(bill._id);
        setViewMode(true);
        setIsDrawerOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.documentUrl) {
            return Swal.fire('Document Required', 'Please upload the bill document (PDF).', 'warning');
        }

        // Sanitize payload: Set session to null if empty string
        const payload = { ...formData };
        if (!payload.session) payload.session = null;

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/minister/bills`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: editingBill ? 'New Version Drafted' : 'Bill Draft Created',
                confirmButtonColor: '#C0722C'
            });

            setIsDrawerOpen(false);
            fetchBills();
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Operation failed. Please check the form data.', 'error');
        }
    };

    const getStatusStyle = (status) => COLORS[status] || COLORS.DRAFT;

    const filteredBills = bills.filter(b =>
        b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container>
            <Header>
                <div>
                    <h1>Legislative Bills</h1>
                    <p>Jurisdiction: <span style={{ color: '#2C1A10', fontWeight: '700' }}>{currentDept?.name}</span></p>
                </div>
                <ActionButton onClick={handleOpenCreate}>
                    <FiPlus size={20} />
                    Draft New Bill
                </ActionButton>
            </Header>

            <SearchContainer>
                <SearchInputWrapper>
                    <div className="search-icon">
                        <FiSearch size={20} />
                    </div>
                    <input
                        placeholder="Search legislation by title or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchInputWrapper>
            </SearchContainer>

            <TableContainer>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '20px' }}>
                        <div style={{ border: '4px solid #F3F3F3', borderTop: '4px solid #C0722C', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' }}></div>
                        <div style={{ fontSize: '11px', color: '#888', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase' }}>Synchronizing Repository...</div>
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : filteredBills.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '450px',
                        textAlign: 'center',
                        animation: 'fadeIn 0.6s ease-out'
                    }}>
                        <div style={{
                            width: '120px',
                            height: '120px',
                            backgroundColor: '#FDF8F3',
                            borderRadius: '45px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#C0722C',
                            marginBottom: '30px'
                        }}>
                            <FiFileText size={56} opacity={0.5} />
                        </div>
                        <h3 style={{ color: '#2C1A10', margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800' }}>Registry is Clear</h3>
                        <p style={{ color: '#888', fontSize: '16px', margin: 0, maxWidth: '350px', lineHeight: '1.6' }}>
                            We couldn't find any legislative drafts matching "<strong>{searchTerm}</strong>". Try adjusting your search keywords.
                        </p>
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{
                                marginTop: '35px',
                                padding: '14px 28px',
                                backgroundColor: 'transparent',
                                border: '2px solid #C0722C',
                                color: '#C0722C',
                                borderRadius: '16px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            Reset Search Registry
                        </button>
                    </div>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Bill Identity</th>
                                <th>Type</th>
                                <th>Version</th>
                                <th>Timeline</th>
                                <th>Status Matrix</th>
                                <th style={{ textAlign: 'right' }}>Directives</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBills.map(bill => (
                                <tr key={bill._id}>
                                    <td>
                                        <div style={{ fontWeight: '800', color: '#2C1A10', fontSize: '17px' }}>{bill.title}</div>
                                        <div style={{ fontSize: '14px', color: '#777', marginTop: '4px' }}>{bill.language}</div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>{bill.type}</div>
                                    </td>
                                    <td><VersionBadge>v{bill.version.toFixed(1)}</VersionBadge></td>
                                    <td>
                                        <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>
                                            {new Date(bill.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </div>
                                    </td>
                                    <td>
                                        {(() => {
                                            const style = getStatusStyle(bill.status);
                                            return (
                                                <StatusBadge bg={style.bg} color={style.text} border={style.border}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: style.text }}></span>
                                                    {bill.status}
                                                </StatusBadge>
                                            );
                                        })()}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <button
                                                onClick={() => handleView(bill)}
                                                style={{ padding: '10px 20px', backgroundColor: '#FDF8F3', color: '#C0722C', border: '1px solid #F0E8DB', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}
                                            >
                                                View
                                            </button>
                                            {bill.status === 'DRAFT' && (
                                                <button
                                                    onClick={() => handleEdit(bill)}
                                                    style={{ padding: '10px 20px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}
                                                >
                                                    Edit
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </TableContainer>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title={viewMode ? "Legislation Details" : (editingBill ? "New Version Draft" : "Legislative Draft Protocol")}
            >
                <div style={{ animation: 'fadeIn 0.4s ease' }}>
                    {viewMode ? (
                        <>
                            <div style={{ marginBottom: '25px', padding: '20px', background: '#FDF8F3', borderRadius: '16px', border: '1px solid rgba(192, 114, 44, 0.1)' }}>
                                <label style={{ display: 'block', fontSize: '10px', fontWeight: '900', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '15px' }}>Version Timeline</label>
                                <div style={{ position: 'relative' }}>
                                    <select
                                        value={selectedVersion}
                                        onChange={e => setSelectedVersion(e.target.value)}
                                        style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #E5E5E5', fontSize: '14px', background: '#fff', outline: 'none' }}
                                    >
                                        {billVersions.map(v => (
                                            <option key={v._id} value={v._id}>
                                                Version {v.version.toFixed(1)} {v.isLatest ? '(Latest)' : ''} - {new Date(v.createdAt).toLocaleDateString()}
                                            </option>
                                        ))}
                                    </select>
                                    <FiLayers style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', pointerEvents: 'none' }} />
                                </div>
                            </div>

                            {editingBill && (
                                <>
                                    <DataBlock>
                                        <label>Legislative Title</label>
                                        <div className="value">{editingBill.title}</div>
                                    </DataBlock>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                        <DataBlock>
                                            <label>Category</label>
                                            <div className="value">{editingBill.type}</div>
                                        </DataBlock>
                                        <DataBlock>
                                            <label>Language</label>
                                            <div className="value">{editingBill.language}</div>
                                        </DataBlock>
                                    </div>

                                    <div style={{ marginBottom: '25px', padding: '20px', background: '#F9F9F9', borderRadius: '16px', border: '1px solid #EEE' }}>
                                        <label style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Description / Objects</label>
                                        <div style={{ fontSize: '14px', color: '#444', lineHeight: '1.7', whiteSpace: 'pre-line' }}>{editingBill.description}</div>
                                    </div>

                                    {editingBill.documentUrl && (
                                        <a
                                            href={editingBill.documentUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '12px',
                                                padding: '16px',
                                                background: '#2C1A10',
                                                color: 'white',
                                                borderRadius: '14px',
                                                textDecoration: 'none',
                                                fontWeight: '800',
                                                boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <FiFileText size={20} /> View Bill PDF Archive
                                        </a>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Legacy Title</label>
                                <input
                                    required
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. The Public Infrastructure Bill, 2026"
                                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Classification</label>
                                    <select
                                        value={formData.type}
                                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                                        style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px', background: '#FAFAFA' }}
                                    >
                                        <option>Ordinary</option>
                                        <option>Money</option>
                                        <option>Financial</option>
                                        <option>Constitution Amendment</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Medium</label>
                                    <select
                                        value={formData.language}
                                        onChange={e => setFormData({ ...formData, language: e.target.value })}
                                        style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px', background: '#FAFAFA' }}
                                    >
                                        <option>English</option>
                                        <option>Hindi</option>
                                        <option>Maithili</option>
                                        <option>Urdu</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Description / Purpose</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Detailed objects and reasons..."
                                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', minHeight: '120px', fontSize: '14px', fontFamily: 'inherit' }}
                                />
                            </div>

                            <div style={{ marginBottom: '30px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Legislative Document (PDF Only)</label>
                                {formData.documentUrl ? (
                                    <div style={{ padding: '16px', background: '#F5FAF6', border: '1px solid #2E7D3230', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#2E7D32' }}>
                                            <FiCheckCircle size={20} />
                                            <span style={{ fontSize: '14px', fontWeight: '700' }}>Document Synchronized</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, documentUrl: '' })}
                                            style={{ background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontWeight: '700' }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <label>
                                        <UploadZone>
                                            <div className="icon"><FiUploadCloud /></div>
                                            <div className="text">{uploading ? 'Processing Architecture...' : 'Upload Official PDF'}</div>
                                            <div className="subtext">Authenticated PDF documents up to 50MB</div>
                                            <input type="file" accept=".pdf" onChange={handleFileUpload} disabled={uploading} />
                                        </UploadZone>
                                    </label>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={uploading}
                                style={{
                                    width: '100%',
                                    padding: '18px',
                                    backgroundColor: '#C0722C',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '16px',
                                    fontWeight: '800',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    boxShadow: '0 10px 25px rgba(192, 114, 44, 0.2)',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {editingBill ? 'Issue New Version' : 'Authenticate & Create Draft'}
                            </button>
                        </form>
                    )}
                </div>
            </SlidingDrawer>
        </Container>
    );
};

export default MinisterBills;
